import React, { createContext, useContext, useMemo } from 'react';
import type { Role } from '@fulcrumapp/fulcrum-core';
import type { User } from '../models/user';
import type { Organization } from '../models/organization';
import type { Membership as GlobalsMembership } from '../models/globals';
import { useApp } from '../hooks/use-app';

// --- Typed Interfaces ---

export interface SessionUser {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  timeZone: string;
  hasTrial: boolean;
}

export interface SessionOrganization {
  id: string;
  name: string;
  apiAccessEnabled: boolean;
  webhooksEnabled: boolean;
  communitiesEnabled: boolean;
  reportingAdvancedEnabled: boolean;
  reportingBasicEnabled: boolean;
  dataEventRecognizetextEnabled: boolean;
  dataEventLoadfileEnabled: boolean;
  dataEventInferenceEnabled: boolean;
  auditLogsEnabled: boolean;
  layersEnabled: boolean;
  sharedViewsEnabled: boolean;
  hasFailedBilling: boolean;
  suspended: boolean;
  maxUsers: number;
  existingUsers: number;
  expired: boolean;
  workflowsEnabled: boolean;
  externalContributionsEnabled: boolean;
  workflowIntegrations: string[];
  workflowIntegrationDailyLimit: number;
  attachmentsEnabled: boolean;
  referenceFilesEnabled: boolean;
  basicIssueResourceId: string | null;
  basicTaskResourceId: string | null;
  connectors: Organization['connectors'];
  esriEnterpriseConnectionEnabled: boolean;
  maxExportRecords: number;
  planName: string;
}

export interface SessionMembership {
  name: string;
  id: string;
  membershipId: string;
  imageSmall: string | null;
  imageLarge: string | null;
  role: GlobalsMembership['role'];
  domain: string | null;
}

export interface Permissions {
  // Role-only permissions (pass-through from fulcrum-core Role)
  canManageSubscription: boolean;
  canUpdateOrganization: boolean;
  canManageMembers: boolean;
  canManageGroups: boolean;
  canManageRoles: boolean;
  canManageApps: boolean;
  canCreateRecords: boolean;
  canUpdateRecords: boolean;
  canRestoreRecords: boolean;
  canBulkDeleteRecords: boolean;
  canBulkUpdateRecords: boolean;
  canDeleteRecords: boolean;
  canManageProjects: boolean;
  canManageChoiceLists: boolean;
  canManageClassificationSets: boolean;
  canChangeStatus: boolean;
  canChangeProject: boolean;
  canChangeLocation: boolean;
  canAssignRecords: boolean;
  canImportRecords: boolean;
  canExportRecords: boolean;
  canRunReports: boolean;
  canManageAuthorizations: boolean;

  // Combined permissions (role + org feature gate)
  canManageLayers: boolean;
  canManageApi: boolean;
  canManageWebhooks: boolean;
  canManageAuditLogs: boolean;

  // Org-derived capabilities
  hasIssuesInstalled: boolean;
  hasTasksInstalled: boolean;
  orgInGoodStanding: boolean;
}

export interface Session {
  user: SessionUser;
  permissions: Permissions;
  organization: SessionOrganization;
  memberships: SessionMembership[];
  isAdminLoginAs: boolean;
}

// --- Mapping Functions ---

export function mapUser(user: User): SessionUser {
  return {
    id: user.id,
    firstName: user.first_name,
    lastName: user.last_name,
    email: user.email,
    timeZone: user.time_zone,
    hasTrial: user.has_trial,
  };
}

export function mapOrganization(org: Organization): SessionOrganization {
  return {
    id: org.id,
    name: org.name,
    apiAccessEnabled: org.api_access_enabled,
    webhooksEnabled: org.webhooks_enabled,
    communitiesEnabled: org.communities_enabled,
    reportingAdvancedEnabled: org.reporting_advanced_enabled,
    reportingBasicEnabled: org.reporting_basic_enabled,
    dataEventRecognizetextEnabled: org.data_event_recognizetext_enabled,
    dataEventLoadfileEnabled: org.data_event_loadfile_enabled,
    dataEventInferenceEnabled: org.data_event_inference_enabled,
    auditLogsEnabled: org.audit_logs_enabled,
    layersEnabled: org.layers_enabled,
    sharedViewsEnabled: org.shared_views_enabled,
    hasFailedBilling: org.has_failed_billing,
    suspended: org.suspended,
    maxUsers: org.max_users,
    existingUsers: org.existing_users,
    expired: org.expired,
    workflowsEnabled: org.workflows_enabled,
    externalContributionsEnabled: org.external_contributions_enabled,
    workflowIntegrations: org.workflow_integrations,
    workflowIntegrationDailyLimit: org.workflow_integration_daily_limit,
    attachmentsEnabled: org.attachments_enabled,
    referenceFilesEnabled: org.reference_files_enabled,
    basicIssueResourceId: org.basic_issue_resource_id,
    basicTaskResourceId: org.basic_task_resource_id,
    connectors: org.connectors,
    esriEnterpriseConnectionEnabled: org.esri_enterprise_connection_enabled,
    maxExportRecords: org.max_export_records,
    planName: org.plan_name,
  };
}

export function mapMemberships(memberships: GlobalsMembership[]): SessionMembership[] {
  return memberships.map((m) => ({
    name: m.name,
    id: m.id,
    membershipId: m.membership_id,
    imageSmall: m.image_small,
    imageLarge: m.image_large,
    role: m.role,
    domain: m.domain,
  }));
}

export function computePermissions(role: Role, org: SessionOrganization): Permissions {
  return {
    // Role-only permissions (pass-through)
    canManageSubscription: !!role._canManageSubscription,
    canUpdateOrganization: !!role._canUpdateOrganization,
    canManageMembers: !!role._canManageMembers,
    canManageGroups: !!role._canManageGroups,
    canManageRoles: !!role._canManageRoles,
    canManageApps: !!role._canManageApps,
    canCreateRecords: !!role._canCreateRecords,
    canUpdateRecords: !!role._canUpdateRecords,
    canRestoreRecords: !!role._canRestoreRecords,
    canBulkDeleteRecords: !!role._canBulkDeleteRecords,
    canBulkUpdateRecords: !!role._canBulkUpdateRecords,
    canDeleteRecords: !!role._canDeleteRecords,
    canManageProjects: !!role._canManageProjects,
    canManageChoiceLists: !!role._canManageChoiceLists,
    canManageClassificationSets: !!role._canManageClassificationSets,
    canChangeStatus: !!role._canChangeStatus,
    canChangeProject: !!role._canChangeProject,
    canChangeLocation: !!role._canChangeLocation,
    canAssignRecords: !!role._canAssignRecords,
    canImportRecords: !!role._canImportRecords,
    canExportRecords: !!role._canExportRecords,
    canRunReports: !!role._canRunReports,
    canManageAuthorizations: !!role._canManageAuthorizations,

    // Combined permissions (role + org feature gate)
    canManageLayers: org.layersEnabled && !!role._canManageLayers,
    canManageApi: org.apiAccessEnabled && !!role._canManageAuthorizations,
    canManageWebhooks: org.webhooksEnabled && !!role._canUpdateOrganization,
    canManageAuditLogs: org.auditLogsEnabled && !!role._canManageRoles,

    // Org-derived capabilities
    hasIssuesInstalled: !!org.basicIssueResourceId,
    hasTasksInstalled: !!org.basicTaskResourceId,
    orgInGoodStanding: !org.suspended && !org.expired,
  };
}

// --- Context and Provider ---

export const SessionContext = createContext<Session | null>(null);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const app = useApp();

  const session = useMemo<Session | null>(() => {
    if (!app.globals.organization || !app.globals.user || !app.globals.memberships) {
      return null;
    }

    const organization = mapOrganization(app.globals.organization);

    return {
      user: mapUser(app.globals.user),
      permissions: computePermissions(app.role, organization),
      organization,
      memberships: mapMemberships(app.globals.memberships),
      isAdminLoginAs: !!app.globals.admin_login_as_active,
    };
  }, [app]);

  return (
    <SessionContext.Provider value={session}>
      {children}
    </SessionContext.Provider>
  );
};

// --- Hooks ---

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return session;
}

export function usePermissions(): Permissions {
  return useSession().permissions;
}

export function useSessionUser(): SessionUser {
  return useSession().user;
}

export function useOrganization(): SessionOrganization {
  return useSession().organization;
}

export function useMemberships(): SessionMembership[] {
  return useSession().memberships;
}
